import { useEffect, useState } from "react";
import { OpenAIAdAccount } from "./OpenAIAdAccount";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Link2, Unplug, RefreshCw, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdConnectionBadge } from "@/components/nadia/Badges";
import { getAdConnections, adsOauthStart, adsSelectAccount, adsDisconnect, adsRemoteCampaigns, formatApiError } from "@/lib/api";

const META = { meta: { label: "Meta", full: "Facebook + Instagram", color: "from-blue-500 to-indigo-500" }, tiktok: { label: "TikTok Ads", full: "TikTok for Business", color: "from-pink-500 to-cyan-400" }, google: { label: "Google Ads", full: "Search · Display · YouTube", color: "from-amber-400 to-emerald-400" } };

export function AdAccounts() {
  const [conns, setConns] = useState(null);
  const [busy, setBusy] = useState(null);
  const [picking, setPicking] = useState(null);
  const [remote, setRemote] = useState({});
  const readRemote = async (p) => {
    setBusy(p);
    try { const r = await adsRemoteCampaigns(p); setRemote({ ...remote, [p]: r }); toast.success(`${r.items.length} campagne lette da ${META[p].label} (dati reali)`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  const [params] = useSearchParams();
  const [loadError, setLoadError] = useState(false);
  const load = () => {
    setLoadError(false);
    return getAdConnections().then(setConns).catch(() => { setConns(null); setLoadError(true); });
  };
  useEffect(() => {
    load();
    const ads = params.get("ads");
    if (ads && params.get("ok")) {
      if (ads === "meta") toast.info("Autorizzazione Meta ricevuta. Verifica lo stato di account e Pagine.");
      else toast.success(`${META[ads]?.label || ads} collegato`);
    }
    if (ads && params.get("error")) toast.error(`Collegamento ${META[ads]?.label || ads} non riuscito: ${params.get("error")}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async (p) => {
    setBusy(p);
    try { const r = await adsOauthStart(p); window.location.href = r.url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(null); }
  };
  const disconnect = async (p) => {
    if (!window.confirm(`Disconnettere ${META[p].label}? Le campagne già pubblicate restano attive sul canale.`)) return;
    setBusy(p);
    try { await adsDisconnect(p); toast.success(`${META[p].label} disconnesso`); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  const select = async (p, account_id, page_id) => {
    setBusy(p);
    try { await adsSelectAccount(p, account_id, page_id); toast.success("Account aggiornato"); setPicking(null); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="ad-accounts">
      <OpenAIAdAccount />
      {Object.entries(META).map(([p, m]) => {
        const c = conns?.[p] || {};
        const connected = c.status === "connected";
        const pending = c.status === "pending";
        return (
          <div key={p} className="nadia-card p-5 flex flex-col gap-3" data-testid={`ad-account-${p}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`font-heading text-base font-semibold bg-gradient-to-r ${m.color} bg-clip-text text-transparent`}>{m.label}</div>
                <div className="text-[11px] text-slate-500">{m.full}</div>
              </div>
              <AdConnectionBadge connection={conns?.[p]} loading={!conns && !loadError} error={loadError} testId={`ad-status-${p}`} />
            </div>
            {(connected || pending) && (
              <div className="text-xs text-slate-300 space-y-1" data-testid={`ad-details-${p}`}>
                <div>Account: <span className="text-white">{c.account_name || c.account_id_ext || "—"}</span>{c.account_id_ext && <span className="text-slate-500"> · {c.account_id_ext}</span>}</div>
                {p === "meta" && <div>Pagina FB: <span className="text-white">{c.page_name || "non selezionata"}</span>{c.instagram_id && <span className="text-slate-500"> · IG collegato</span>}</div>}
                <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid={`ad-state-chips-${p}`}>
                  <StateChip provider={p} ok={c.oauth_authorized} label="OAuth" />
                  <StateChip provider={p} ok={c.account_selected} label="Account" />
                  {p === "meta" && <StateChip provider={p} ok={c.page_selected} label="Pagina FB" />}
                  <StateChip provider={p} ok={c.api_verified} label="Verificato via API" />
                  <StateChip provider={p} ok={c.publish_ready} label="Pronto a pubblicare" strong />
                </div>
                {!c.publish_ready && (c.blockers || []).length > 0 && (
                  <ul className="text-[11px] text-amber-300/90 list-disc pl-4 space-y-0.5" data-testid={`ad-blockers-${p}`}>
                    {c.blockers.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
              </div>
            )}
            {picking === p && (
              <div className="space-y-2 text-xs" data-testid={`ad-picker-${p}`}>
                {(c.accounts || []).map((a) => (
                  <button key={a.id} onClick={() => select(p, a.id, p === "meta" ? (c.pages || [])[0]?.id : undefined)} className="w-full text-left rounded-lg border border-white/10 px-3 py-2 hover:border-cyan-400/40" data-testid={`ad-pick-${p}-${a.id}`}>{a.name || a.id} <span className="text-slate-500">· {a.id}</span></button>
                ))}
                {p === "meta" && c.account_id_ext && (c.pages || []).map((pg) => (
                  <button key={pg.id} data-testid={`ad-pick-page-${pg.id}`} onClick={() => select(p, c.account_id_ext, pg.id)} className="w-full text-left rounded-lg border border-white/10 px-3 py-2 hover:border-cyan-400/40">Pagina: {pg.name}{pg.instagram_username ? ` · @${pg.instagram_username}` : ""}</button>
                ))}
                {!(c.accounts || []).length && (
                  <div className="text-slate-500" data-testid={`ad-noaccounts-${p}`}>
                    {c.accounts_error
                      ? `Il provider non restituisce account: ${c.accounts_error}`
                      : "Nessun account pubblicitario trovato su questo login."}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-auto">
              {!connected && !pending ? (
                <Button size="sm" onClick={() => connect(p)} disabled={busy === p} className={`bg-gradient-to-r ${m.color} text-slate-950 font-semibold`} data-testid={`ad-connect-${p}`}>
                  {busy === p ? <Loader2 size={13} className="animate-spin mr-1" /> : <Link2 size={13} className="mr-1" />} Collega {m.label}
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={() => setPicking(picking === p ? null : p)} className="border-white/10" data-testid={`ad-change-${p}`}><RefreshCw size={13} className="mr-1" /> Cambia account</Button>
                  {connected && <Button size="sm" variant="outline" onClick={() => readRemote(p)} disabled={busy === p} className="border-cyan-400/30 text-cyan-200" data-testid={`ad-read-${p}`}>Leggi campagne</Button>}
                  <Button size="sm" variant="outline" onClick={() => disconnect(p)} disabled={busy === p} className="border-rose-500/30 text-rose-200 hover:bg-rose-500/10" data-testid={`ad-disconnect-${p}`}><Unplug size={13} className="mr-1" /> Disconnetti</Button>
                </>
              )}
            </div>
            {remote[p] && (
              <div className="text-[11px] text-slate-400 space-y-0.5" data-testid={`ad-remote-${p}`}>
                <div className="text-emerald-300">Lettura reale · account {remote[p].account_name || remote[p].account_id}</div>
                {remote[p].items.slice(0, 5).map((c) => <div key={c.id} className="truncate">• {c.name} <span className="text-slate-600">· {c.id} · {c.status}</span></div>)}
                {!remote[p].items.length && <div>Nessuna campagna presente sull'account.</div>}
              </div>
            )}
            {!c.app_configured && <div className="text-[10px] text-amber-300/80" data-testid={`ad-appmissing-${p}`}>Configurazione necessaria: l'app del provider non è ancora collegata al server Nadia.</div>}
          </div>
        );
      })}
    </div>
  );
}

function StateChip({ provider, ok, label, strong }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
        ok ? (strong ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" : "border-emerald-500/25 text-emerald-300")
           : "border-white/10 text-slate-500"
      }`}
      data-testid={`ad-chip-${provider}-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />}{label}
    </span>
  );
}
